import { ArrowRight, BriefcaseBusiness, Building2, MapPin } from "lucide-react";
import { motion } from "framer-motion";

type WhyChicagoProps = {
  onApplyClick: () => void;
};

const chicagoImage =
  "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?q=85&w=1800&auto=format&fit=crop";

const WhyChicago = ({ onApplyClick }: WhyChicagoProps) => {
  return (
    <section className="w-full overflow-hidden bg-[#F7F9FC] py-16 ">
      <div className="container-content">

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-edept-orange">
            Your next chapter
          </p>

          <h2 className="mt-3 text-3xl leading-tight text-edept-dark sm:text-4xl lg:text-5xl">
            Why Chicago?
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
            Because your U.S. experience should be more than a degree.
            It should put you in the middle of a city where business,
            technology, culture and opportunity come together.
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-8">

          {/* Chicago Image */}
          <motion.div
            initial={{ opacity: 0, x: -25 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="group relative min-h-[420px] overflow-hidden rounded-3xl lg:min-h-[560px]"
          >
            <img
              src={chicagoImage}
              alt="Chicago skyline and city architecture"
              className="
                absolute
                inset-0
                h-full
                w-full
                object-cover
                transition-transform
                duration-700
                ease-[cubic-bezier(0.22,1,0.36,1)]
                group-hover:scale-[1.04]
              "
            />

            {/* Image gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />

            {/* Image Content */}
            <div className="absolute bottom-0 left-0 right-0 p-7 sm:p-9">
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-white/80">
                <MapPin size={16} />
                Chicago, Illinois
              </div>

              <h3 className="max-w-lg text-3xl font-semibold leading-tight text-white sm:text-4xl">
                Study where the city itself becomes part of your education.
              </h3>

              <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/75 sm:text-base">
                Experience life in one of America's most dynamic metropolitan
                cities while completing your U.S. degree.
              </p>
            </div>
          </motion.div>

          {/* Right Content */}
          <motion.div
            initial={{ opacity: 0, x: 25 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="
              flex
              flex-col
              justify-between
              rounded-3xl
              bg-white
              p-7
              shadow-sm
              sm:p-9
              lg:p-10
            "
          >
            <div>

              <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[#12539F]">
                From a student's perspective
              </p>

              <h3 className="mt-3 text-2xl font-semibold leading-tight text-edept-dark sm:text-3xl">
                Imagine spending your next semester here.
              </h3>

              <p className="mt-4 text-base leading-relaxed text-slate-600">
                You're not just moving to another campus. You're stepping into
                a city that gives you a different way of learning, living and
                thinking.
              </p>

              {/* Benefits */}
              <div className="mt-8 space-y-6">

                {/* Benefit 1 */}
                <div className="flex gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#12539F]/10 text-[#12539F]">
                    <BriefcaseBusiness size={21} />
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Be closer to opportunity
                    </h4>

                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      Chicago is a major business and technology hub, giving
                      you exposure to the professional environment beyond the
                      classroom.
                    </p>
                  </div>
                </div>

                {/* Benefit 2 */}
                <div className="flex gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F5A623]/15 text-[#C47B00]">
                    <Building2 size={21} />
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Experience a global city
                    </h4>

                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      Meet people from different backgrounds, experience a
                      global culture and build the independence that comes
                      with living in the U.S.
                    </p>
                  </div>
                </div>

                {/* Benefit 3 */}
                <div className="flex gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#12539F]/10 text-[#12539F]">
                    <MapPin size={21} />
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Make your U.S. experience count
                    </h4>

                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      Study on campus, experience the city and build memories
                      that go far beyond your academic journey.
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-10 border-t border-slate-100 pt-7">
              <p className="text-sm text-slate-500">
                One decision can change where your career takes you.
              </p>

              <button
                type="button"
                onClick={onApplyClick}
                className="
                  mt-4
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  bg-[#12539F]
                  px-6
                  py-3
                  text-sm
                  font-semibold
                  text-white
                  transition-all
                  hover:bg-[#0E4687]
                  hover:shadow-lg
                "
              >
                Start Your Chicago Journey
                <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default WhyChicago;